import './faq.less'

import {Icon} from '@codeurs/front'
import {Block, BlockType} from 'blocks/block'
import {Blocks} from 'blocks/blocks'
import queryString from 'query-string'
import React, {useEffect, useRef, useState} from 'react'
import AnimateHeight from 'react-animate-height'
import {Link} from 'react-router-dom'
import {PageData, WysiwygType} from 'types'
import {style} from 'util/style'
import {Hero, HeroType} from 'views/hero/hero'
import {Layout} from 'views/layout'
import {Theme} from 'views/theme'

const styles = style('faq', ['overview', 'category', 'item'])

type FaqOverviewCategoryType = {
	title: string
	path: string
	children: Array<FaqOverviewItemType>
}

type FaqOverviewItemType = {
	catPath: string
	title: string
	path: string
	question: string
	answer: WysiwygType
}

export const Faq: React.FC<PageData & {
	hero: HeroType
	blocks: Array<BlockType>
	categories: Array<FaqOverviewCategoryType>
}> = data => {
	const {title, hero, blocks, categories} = data

	return (
		<Layout page={data}>
			<div class={styles()}>
				<Hero pagetitle={title} {...hero} />
				<FaqOverview categories={categories} />
				{blocks && blocks.length !== 0 && (
					<Blocks className={styles('blocks')}>
						{blocks.map((block, i) => (
							<Block key={i} {...block} />
						))}
					</Blocks>
				)}
			</div>
		</Layout>
	)
}

export const FaqOverview: React.FC<{
	categories: Array<FaqOverviewCategoryType>
}> = ({categories}) => {
	if (!categories || categories.length === 0) return null

	return (
		<div className={styles.overview()}>
			<Theme.OuterContainer>
				<Theme.Container>
					{categories.map(item => (
						<FaqOverviewCategory key={item.path} {...item} />
					))}
				</Theme.Container>
			</Theme.OuterContainer>
		</div>
	)
}

const FaqOverviewCategory: React.FC<FaqOverviewCategoryType> = ({
	title,
	path,
	children
}) => {
	const ref = useRef(null)
	const [isOpen, setOpen] = useState(true)
	if (!title || !children) return null

	useEffect(() => {
		const {cat, q} = queryString.parse(location.search)
		if (cat !== path) return
		setOpen(true)
		if (!q) ref.current.scrollIntoView({behavior: 'smooth'})
	}, [])

	return (
		<div ref={ref} className={styles.category()}>
			<Link
				to={!isOpen ? `?cat=${path}` : '?'}
				replace
				onClick={() => setOpen(!isOpen)}
				className={styles
					.category('block')
					.is({open: isOpen})
					.toString()}
			>
				<Theme.H5 as="h3" className={styles.category('block-title')}>
					{title}
				</Theme.H5>
				<div className={styles.category('block-icon')}>
					<div className={styles.category('block-icon-icon')}>
						<Icon icon="chevron-down-wide" />
					</div>
				</div>
			</Link>
			<AnimateHeight height={isOpen ? 'auto' : 0}>
				{children.map(item => (
					<FaqOverviewItem key={item.path} catPath={path} {...item} />
				))}
			</AnimateHeight>
		</div>
	)
}

const FaqOverviewItem: React.FC<FaqOverviewItemType> = ({
	catPath,
	title,
	path,
	question,
	answer
}) => {
	const ref = useRef(null)

	if (!title || !answer) return null

	useEffect(() => {
		const {cat, q} = queryString.parse(location.search)
		if (cat !== catPath || !q || q !== path) return
		ref.current.scrollIntoView({behavior: 'smooth'})
	}, [])

	return (
		<Link
			ref={ref}
			to={`?cat=${catPath}&q=${path}`}
			replace
			className={styles.item().toString()}
		>
			<Theme.H5 className={styles.item('title')}>{question || title}</Theme.H5>
			<div className={styles.item('text')}>
				<Theme.Wysiwyg>{answer}</Theme.Wysiwyg>
			</div>
		</Link>
	)
}
